// SolyticsModal.jsx
// @ts-nocheck
/* eslint-disable react/prop-types */
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import SolyticsBox from './SolyticsBox';
import SolyticsTypography from './SolyticsTypography';
import SolyticsButton from './SolyticsButton';
import SolyticsGrid from './SolyticsGrid';
import { ReactComponent as WarningAmberIcon } from '../../assets/Icons/lucideIcons/triangle-alert.svg';
import { ReactComponent as CheckCircleIcon } from '../../assets/Icons/lucideIcons/circle-check.svg';
import { ReactComponent as ErrorIcon } from '../../assets/Icons/lucideIcons/circle-alert.svg';
import { ReactComponent as CloseIcon } from '../assets/CloseIcon.svg';
import { ReactComponent as InfoIcon } from '../assets/InfoIcon.svg';
import SolyticsDivider from './SolyticsDivider';

/**
 * SolyticsModal - A configurable dialog/modal component
 *
 * @component
 * @description
 * SolyticsModal wraps MUI Dialog with a standardized layout: title bar with optional status icon,
 * secondary header, info banner, scrollable content area (SolyticsGrid), and action buttons.
 * Supports read-only mode, custom actions, and status indicators.
 *
 * @param {string} [id] - Custom id prefix for all modal elements (falls back to slugified title)
 * @param {function} handleClose - Close handler called when close button or cancel is clicked
 * @param {boolean} open - Controls dialog visibility
 * @param {function} [onSave] - Save/submit handler for the primary action button
 * @param {function} [onReset] - Reset handler (shown when showReset is true)
 * @param {React.ReactNode} dialogContent - Content rendered inside the dialog body
 * @param {string} title - Dialog title text
 * @param {string} [secondaryHeader=''] - Subtitle text below the title
 * @param {string} [infoText=''] - Informational banner text shown above content
 * @param {boolean} [actionShow=true] - If true, shows the action buttons footer
 * @param {boolean} [showReset=false] - If true, shows a reset button
 * @param {boolean} [isEditMode=false] - If true, primary button shows "Update" instead of "Save"
 * @param {boolean} [readOnly=false] - If true, shows only a close button in actions
 * @param {string} [maxWidth='sm'] - MUI Dialog maxWidth: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
 * @param {boolean} [fullScreen=false] - If true, Dialog renders full screen
 * @param {React.ReactNode} [actions] - Custom action buttons (replaces default Save/Cancel)
 * @param {boolean} [showStatus=false] - If true, shows a status icon next to the title
 * @param {string} [status='completed'] - Status type: 'completed' | 'pending' | 'failed'
 *
 * @returns {React.ReactElement|null} A dialog element, or null if no title and no content
 */

const MODEL_STATUS_ICONS = {
  completed: {
    icon: CheckCircleIcon,
    color: (theme) => theme?.palette?.success?.main,
  },
  pending: {
    icon: WarningAmberIcon,
    color: (theme) => theme?.palette?.warning?.main,
  },
  failed: {
    icon: ErrorIcon,
    color: (theme) => theme?.palette?.error?.main,
  },
};

// Utility to generate WCAG-safe slug
const slugify = (str = '') =>
  str
    ?.toLowerCase()
    ?.replace(/[^a-z0-9]+/g, '-')
    ?.replace(/(^-|-$)/g, '');

const SolyticsModal = ({
  id,
  handleClose,
  open,
  onSave = () => {},
  onReset = () => {},
  dialogContent,
  title,
  secondaryHeader = '',
  infoText = '',
  actionShow = true,
  showReset = false,
  isEditMode = false,
  readOnly = false,
  maxWidth = 'sm',
  fullScreen = false,
  actions,
  showStatus = false,
  status = 'completed',
}) => {
  if (!title && !dialogContent) return null;

  const idSlug = id ?? slugify(title);
  const statusInfo = MODEL_STATUS_ICONS[status];

  return (
    <Dialog
      open={open}
      fullWidth
      fullScreen={fullScreen}
      maxWidth={maxWidth}
      aria-labelledby={`modal-title-${idSlug}`}
      aria-describedby={`modal-content-${idSlug}`}
      sx={{ overflow: 'hidden', '& .MuiDialog-paper': { borderRadius: '4px' } }}
      onClose={(e, reason) => {
        if (reason !== 'backdropClick') handleClose();
      }}
    >
      {/* LEFT HEADER BLOCK */}
      <DialogTitle
        id={`modal-title-${idSlug}`}
        sx={{ display: 'flex', alignItems: 'start', flexDirection: 'column' }}
      >
        <SolyticsBox
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <SolyticsBox sx={{ display: 'flex', alignItems: 'center' }}>
            {/* STATUS ICON LEFT OF TITLE */}
            {showStatus && statusInfo && (
              <SolyticsBox
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: (theme) => statusInfo?.color(theme),
                  mr: 1,
                }}
                id={`modal-status-${idSlug}`}
              >
                <statusInfo.icon />
              </SolyticsBox>
            )}

            <SolyticsTypography variant="h4">{title}</SolyticsTypography>
          </SolyticsBox>
          {/* CLOSE BUTTON */}
          <IconButton
            id={`modal-close-btn-${idSlug}`}
            onClick={handleClose}
            title="Close"
          >
            <CloseIcon style={{ height: '24px', width: '24px' }} />
          </IconButton>
        </SolyticsBox>
        {secondaryHeader?.trim()?.length > 0 && (
          <SolyticsTypography
            variant="body1"
            sx={{ color: (theme) => theme?.palette?.text?.secondary }}
          >
            {secondaryHeader}
          </SolyticsTypography>
        )}
      </DialogTitle>

      <SolyticsDivider />

      {/* CONTENT AREA */}
      <DialogContent id={`modal-content-${idSlug}`}>
        {infoText?.trim()?.length > 0 && (
          <SolyticsBox
            sx={{
              backgroundColor: (theme) =>
                theme?.palette?.background?.infoBackground,
              color: (theme) => theme?.palette?.skyBlue?.icon,
              p: 1.5,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 2,
              '& svg': {
                width: '20px',
                height: '20px',
                fill: 'currentColor',
              },
            }}
          >
            <InfoIcon />
            <SolyticsTypography variant="subtitle1">
              {infoText}
            </SolyticsTypography>
          </SolyticsBox>
        )}

        <SolyticsGrid container spacing={2}>
          {dialogContent}
        </SolyticsGrid>
      </DialogContent>

      {/* ACTION BUTTONS */}
      {actionShow && !readOnly && (
        <DialogActions id={`modal-actions-${idSlug}`} sx={{ pb: 3, px: 3 }}>
          {actions ? (
            actions
          ) : (
            <>
              {showReset && (
                <SolyticsButton
                  id={`modal-reset-btn-${idSlug}`}
                  onClick={onReset}
                  variant="contained"
                  color="secondary"
                >
                  Reset
                </SolyticsButton>
              )}

              <SolyticsButton
                id={`modal-cancel-btn-${idSlug}`}
                onClick={handleClose}
                variant="contained"
                color="secondary"
              >
                Cancel
              </SolyticsButton>

              <SolyticsButton
                id={`modal-save-btn-${idSlug}`}
                onClick={onSave}
                variant="contained"
              >
                {isEditMode ? 'Update' : 'Save'}
              </SolyticsButton>
            </>
          )}
        </DialogActions>
      )}

      {/* READ ONLY MODE */}
      {readOnly && actionShow && (
        <DialogActions
          id={`modal-readonly-actions-${idSlug}`}
          sx={{ pb: 3, px: 3 }}
        >
          <SolyticsButton
            id={`modal-readonly-close-${idSlug}`}
            onClick={handleClose}
            variant="outlined"
          >
            Close
          </SolyticsButton>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default SolyticsModal;

/**
 * ## Features
 * - Title bar with close button and optional status icon (completed/pending/failed)
 * - Secondary header for subtitles
 * - Info banner with icon for contextual messages
 * - Scrollable content area using SolyticsGrid
 * - Default action buttons: Cancel + Save/Update, with optional Reset
 * - Custom actions support via `actions` prop
 * - Read-only mode shows only a Close button
 * - Backdrop click does not close the dialog (intentional UX)
 * - WCAG compliant with aria-labelledby and aria-describedby
 * - Auto-generated WCAG-safe id slugs for all interactive elements
 *
 * ## Examples
 *
 * ```jsx
 * // Basic modal
 * <SolyticsModal
 *   open={isOpen}
 *   handleClose={() => setIsOpen(false)}
 *   title="Create Record"
 *   dialogContent={<MyForm />}
 *   onSave={handleSave}
 * />
 *
 * // Edit mode with status
 * <SolyticsModal
 *   open={isOpen}
 *   handleClose={handleClose}
 *   title="Edit Model"
 *   isEditMode
 *   showStatus
 *   status="pending"
 *   dialogContent={<EditForm />}
 *   onSave={handleUpdate}
 * />
 *
 * // Read-only view
 * <SolyticsModal
 *   open={isOpen}
 *   handleClose={handleClose}
 *   title="View Details"
 *   readOnly
 *   dialogContent={<DetailView />}
 * />
 * ```
 */
